package fyi.moko.hotpl.data.backup

import fyi.moko.hotpl.data.repository.JsPlaceRepositoryImpl
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceIdGenerator
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.RandomPlaceIdGenerator
import fyi.moko.hotpl.domain.model.RevisitCadence
import fyi.moko.hotpl.domain.model.CostFeel
import fyi.moko.hotpl.domain.model.SyncState
import fyi.moko.hotpl.domain.model.Visit
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.service.AuthService
import kotlin.time.Clock

class JsBackupManager(
    private val placeRepository: JsPlaceRepositoryImpl,
    private val authService: AuthService,
    private val now: () -> Long = { Clock.System.now().toEpochMilliseconds() },
    private val idGenerator: PlaceIdGenerator = RandomPlaceIdGenerator,
) : LocalBackupManager {

    override suspend fun exportJson(): String {
        val places = placeRepository.getAllPlacesRaw().filter { !it.isDeleted }.sortedBy { it.createdAt }
        val visitsByPlaceId = placeRepository.getAllVisitsRaw().groupBy { it.placeId }

        val file = HotplBackupFile(
            formatVersion = HotplBackupJsonCodec.FORMAT_VERSION,
            exportedAt = now(),
            places = places.map { place ->
                HotplBackupPlace(
                    name = place.name,
                    note = place.note,
                    locationContext = place.locationContext,
                    tags = place.tags,
                    liked = place.liked,
                    latitude = place.latitude,
                    longitude = place.longitude,
                    createdAt = place.createdAt,
                    updatedAt = place.updatedAt,
                    version = place.version,
                    revisitCadence = place.revisitCadence?.name,
                    costFeel = place.costFeel?.name,
                    status = place.status.name,
                    visits = visitsByPlaceId[place.id].orEmpty()
                        .sortedBy { it.visitedAt }
                        .map { visit ->
                            HotplBackupVisit(visitedAt = visit.visitedAt, note = visit.note, emotion = visit.emotion)
                        },
                )
            },
        )
        return HotplBackupJsonCodec.encode(file)
    }

    override suspend fun prepareImport(json: String): PreparedHotplBackup {
        return HotplBackupJsonCodec.decode(json)
    }

    override suspend fun restore(prepared: PreparedHotplBackup) {
        val uid = authService.getCurrentUserId() ?: ""
        val newPlaces = mutableListOf<Place>()
        val newVisits = mutableListOf<Visit>()

        for (backupPlace in prepared.file.places) {
            val placeId = idGenerator.newId()
            newPlaces.add(
                Place(
                    id = placeId,
                    name = backupPlace.name,
                    note = backupPlace.note,
                    locationContext = backupPlace.locationContext,
                    tags = backupPlace.tags,
                    liked = backupPlace.liked,
                    latitude = backupPlace.latitude,
                    longitude = backupPlace.longitude,
                    createdAt = backupPlace.createdAt,
                    updatedAt = backupPlace.updatedAt,
                    syncState = SyncState.PENDING_UPSERT,
                    ownerId = uid,
                    version = 1L,
                    revisitCadence = backupPlace.revisitCadence?.let { runCatching { RevisitCadence.valueOf(it) }.getOrNull() },
                    costFeel = backupPlace.costFeel?.let { runCatching { CostFeel.valueOf(it) }.getOrNull() },
                    status = backupPlace.status?.let { runCatching { PlaceStatus.valueOf(it) }.getOrNull() } ?: PlaceStatus.VISITED,
                )
            )

            for (backupVisit in backupPlace.visits) {
                newVisits.add(
                    Visit(
                        id = idGenerator.newId(),
                        placeId = placeId,
                        note = backupVisit.note,
                        visitedAt = backupVisit.visitedAt,
                        emotion = backupVisit.emotion
                    )
                )
            }
        }

        placeRepository.setAll(newPlaces, newVisits)
    }

    override suspend fun merge(places: List<HotplBackupPlace>) {
        val uid = authService.getCurrentUserId() ?: ""
        if (places.isEmpty()) return

        val currentPlaces = placeRepository.getAllPlacesRaw().associateBy { normalizeForComparison(it.name) }.toMutableMap()
        val currentVisits = placeRepository.getAllVisitsRaw().toMutableList()

        for (backupPlace in places) {
            val normalizedName = normalizeForComparison(backupPlace.name)
            val matchedLocal = currentPlaces[normalizedName]

            if (matchedLocal == null) {
                val placeId = idGenerator.newId()
                val newPlace = Place(
                    id = placeId,
                    name = backupPlace.name,
                    note = backupPlace.note,
                    locationContext = backupPlace.locationContext,
                    tags = backupPlace.tags,
                    liked = backupPlace.liked,
                    latitude = backupPlace.latitude,
                    longitude = backupPlace.longitude,
                    createdAt = backupPlace.createdAt,
                    updatedAt = backupPlace.updatedAt,
                    syncState = SyncState.PENDING_UPSERT,
                    ownerId = uid,
                    version = 1L,
                    revisitCadence = backupPlace.revisitCadence?.let { runCatching { RevisitCadence.valueOf(it) }.getOrNull() },
                    costFeel = backupPlace.costFeel?.let { runCatching { CostFeel.valueOf(it) }.getOrNull() },
                    status = backupPlace.status?.let { runCatching { PlaceStatus.valueOf(it) }.getOrNull() } ?: PlaceStatus.VISITED,
                )
                currentPlaces[normalizedName] = newPlace

                for (v in backupPlace.visits) {
                    currentVisits.add(
                        Visit(
                            id = idGenerator.newId(),
                            placeId = placeId,
                            note = v.note,
                            visitedAt = v.visitedAt,
                            emotion = v.emotion
                        )
                    )
                }
            } else {
                val isBackupNewer = if (backupPlace.version != null && matchedLocal.version != null) {
                    if (backupPlace.version > matchedLocal.version) {
                        true
                    } else if (backupPlace.version < matchedLocal.version) {
                        false
                    } else {
                        backupPlace.updatedAt > matchedLocal.updatedAt
                    }
                } else {
                    backupPlace.updatedAt > matchedLocal.updatedAt
                }

                if (isBackupNewer) {
                    val updatedPlace = matchedLocal.copy(
                        note = backupPlace.note,
                        locationContext = backupPlace.locationContext,
                        tags = backupPlace.tags,
                        liked = backupPlace.liked,
                        latitude = backupPlace.latitude,
                        longitude = backupPlace.longitude,
                        updatedAt = backupPlace.updatedAt,
                        syncState = SyncState.PENDING_UPSERT,
                        ownerId = uid,
                        version = 1L,
                        revisitCadence = backupPlace.revisitCadence?.let { runCatching { RevisitCadence.valueOf(it) }.getOrNull() },
                        costFeel = backupPlace.costFeel?.let { runCatching { CostFeel.valueOf(it) }.getOrNull() },
                        status = backupPlace.status?.let { runCatching { PlaceStatus.valueOf(it) }.getOrNull() } ?: PlaceStatus.VISITED,
                    )
                    currentPlaces[normalizedName] = updatedPlace

                    // Remove current visits for this place
                    currentVisits.removeAll { it.placeId == matchedLocal.id }

                    // Add backup visits
                    for (v in backupPlace.visits) {
                        currentVisits.add(
                            Visit(
                                id = idGenerator.newId(),
                                placeId = matchedLocal.id,
                                note = v.note,
                                visitedAt = v.visitedAt,
                                emotion = v.emotion
                            )
                        )
                    }
                }
            }
        }

        placeRepository.setAll(currentPlaces.values.toList(), currentVisits)
    }
}
